import {AfterViewInit, Component, inject, OnDestroy, OnInit, ViewChild} from '@angular/core';
import {IConnectedClientInfo} from "../../interfaces/IConnectedClientInfo";
import {SignalRService} from "../../services/signal-r.service";
import {ChooseYourGameComponent} from "../../components/choose-your-game/choose-your-game.component";
import {ContainerComponent} from "@coreui/angular";
import {ReportsComponent} from "../reports/reports.component";
import {interval, Observable, Subscription, tap} from "rxjs";
import {AsyncPipe, DatePipe, NgForOf, NgIf, NgSwitch, NgSwitchCase, NgSwitchDefault} from "@angular/common";
import {RouterLink} from "@angular/router";
import {FormsModule, ReactiveFormsModule} from "@angular/forms";
import {MatError, MatFormField, MatFormFieldModule, MatLabel} from "@angular/material/form-field";
import {MatInput, MatInputModule} from "@angular/material/input";
import {MatOption} from "@angular/material/autocomplete";
import {MatSelect, MatSelectChange} from "@angular/material/select";
import {MatIconModule} from "@angular/material/icon";
import {MatButtonModule} from "@angular/material/button";
import {MatTableDataSource, MatTableModule} from "@angular/material/table";
import {MatPaginator} from "@angular/material/paginator";
import {MatCard, MatCardContent} from "@angular/material/card";
import {PipesModule} from "../../pipes/pipes.module";

@Component({
  selector: 'app-signal-r-connections',
  standalone: true,
  imports: [
    ChooseYourGameComponent,
    ContainerComponent,
    ReportsComponent,
    AsyncPipe,
    NgIf,
    NgForOf,
    DatePipe,
    RouterLink,
    FormsModule,
    MatError,
    MatFormField,
    MatInput,
    MatLabel,
    ReactiveFormsModule,
    MatOption,
    MatSelect,
    MatFormFieldModule,
    MatInputModule,
    MatButtonModule,
    MatIconModule,
    MatTableModule,
    NgSwitch,
    NgSwitchCase,
    NgSwitchDefault,
    MatPaginator,
    MatCard,
    MatCardContent,
    PipesModule
  ],
  templateUrl: './signal-r-connections.component.html',
  styleUrl: './signal-r-connections.component.css'
})
export class SignalRConnectionsComponent implements OnInit, AfterViewInit, OnDestroy {

  private signalRService: SignalRService = inject(SignalRService);

  public connectedClients$: Observable<IConnectedClientInfo[]> = this.signalRService.connectedClients$.pipe(tap((res) => {
    this.originalConnections = res;
    this.filterConnections();
  }));

  public subscription: Subscription | null = null;
  private timerSubscription: Subscription | null = null;
  protected originalConnections: IConnectedClientInfo[] = [];

  // Filter properties
  protected gameIdFilter: string = '';
  protected userTypeFilter: string = '';
  protected currentTime: Date = new Date();

  // Table related properties
  displayedColumns: string[] = ['userType', 'userId', 'connectionId', 'gameId', 'connectedAt', 'duration'];
  dataSource: MatTableDataSource<IConnectedClientInfo> = new MatTableDataSource<IConnectedClientInfo>([]);

  @ViewChild(MatPaginator) paginator!: MatPaginator;

  ngOnInit() {
    this.signalRService.listenToConnectedClientChanges();
    this.subscription = this.connectedClients$.subscribe();

    // Update currentTime every minute
    this.timerSubscription = interval(60000).subscribe(() => {
      this.currentTime = new Date();
    });
  }

  ngAfterViewInit() {
    // Set the paginator after the view initializes
    this.dataSource.paginator = this.paginator;
  }

  // Combined filtering function: filter the original connections using both filters
  filterConnections() {
    const filtered = this.originalConnections.filter(connection => {
      const gameMatch = !this.gameIdFilter || connection.GameId === this.gameIdFilter;
      const userTypeMatch = !this.userTypeFilter || connection.UserType === this.userTypeFilter;
      return gameMatch && userTypeMatch;
    });
    // Update the table data source
    this.dataSource.data = filtered;
  }

  // Update gameId filter and reapply filtering
  filterOnGameType(gameId: string) {
    this.gameIdFilter = gameId;
    this.filterConnections();
  }

  // Update userType filter and reapply filtering
  filterOnUserType(userTypeChangeP: MatSelectChange) {
    this.userTypeFilter = userTypeChangeP.value || '';
    this.filterConnections();
  }

  ngOnDestroy(): void {
    if (this.subscription) {
      this.subscription.unsubscribe();
    }
    if (this.timerSubscription) {
      this.timerSubscription.unsubscribe();
    }
  }
}
